import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import useBasename from '../../../../components/routing/useBasename';
import { isAlbum, isMemoryBook, isVideoAlbum } from '../../../../util/album';
import { MeQuery } from '../../../../graphql/queries/account';
import useAuthenticated from '../../../../hooks/useAuthenticated';
import ListingCardTemplateThumbnail from './components/ListingCardTemplateThumbnail';
import ListingCardTemplateLink from './components/ListingCardTemplateLink';
import GridMoveIcon from '../../../../components/icons/GridMoveIcon';

const propTypes = {
  className: PropTypes.string,
  linkTo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onClick: PropTypes.func,
  icon: PropTypes.node,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  isDisabled: PropTypes.bool,
  isActive: PropTypes.bool,
  entity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_video_album: PropTypes.bool,
    is_memory_book: PropTypes.bool,
  }),
  thumbnailUrl: PropTypes.string,
  thumbnailActions: PropTypes.node,
  thumbnailNullText: PropTypes.string,
  thumbnailChildren: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  note: PropTypes.node,
  info: PropTypes.node,
  nullState: PropTypes.node,
  sortHandleProps: PropTypes.object,
  templateLinkProps: PropTypes.object,
};

const ListingCardTemplate = ({
  className,
  linkTo,
  onClick,
  icon,
  title,
  subtitle,
  isDisabled,
  isActive,
  entity,
  thumbnailUrl,
  thumbnailActions,
  thumbnailNullText = 'No photos yet',
  thumbnailChildren,
  note,
  info,
  nullState, // Helpful when using this template outside of PM.
  sortHandleProps,
  templateLinkProps,
}) => {
  // Because permissions have been introduced in the template
  // In order to avoid calling the inner API, we'll compare current account
  // with entity's creator/account.
  const authenticated = useAuthenticated();
  const { data: { me: account } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });

  const basename = useBasename();
  const isMemoryBookBool = isAlbum(entity) && isMemoryBook(entity);
  const isGroupVideoBool = isAlbum(entity) && isVideoAlbum(entity);

  const classes = classNames({
    'listing-card-template': true,
    'has-thumbnail': !!thumbnailUrl,
    'is-disabled': isDisabled,
    'is-active': isActive,
    [className]: !!className,
    'group-video-card-template': isGroupVideoBool,
    'memory-book-card-template': isMemoryBookBool,
  });

  const contentClasses = classNames({
    'listing-card-template-content': true,
  });

  return (
    <div className={classes}>
      <div className={contentClasses}>
        {icon && (
          <span className="listing-card-template-cover-icon">{icon}</span>
        )}
        <ListingCardTemplateLink
          linkTo={linkTo}
          disabled={isDisabled}
          onClick={onClick}
          {...(templateLinkProps ?? {})}
        >
          <p className="listing-card-template-title">{title}</p>

          {subtitle && (
            <div className="listing-card-template-content-footer">
              {subtitle && (
                <span className="listing-card-template-subtitle">
                  {subtitle}
                </span>
              )}
            </div>
          )}
        </ListingCardTemplateLink>

        {sortHandleProps && (
          <button
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="btn-reset d-flex align-items-center justify-content-center"
            {...sortHandleProps}
          >
            <GridMoveIcon size={30} color="#000000" />
          </button>
        )}
        {thumbnailActions && (
          <div className="listing-card-template-actions">
            {thumbnailActions}
          </div>
        )}
      </div>
      <figure className="listing-card-template-cover">
        <ListingCardTemplateThumbnail
          title={title}
          entity={entity}
          account={account}
          linkTo={linkTo}
          nullState={nullState}
          basename={basename}
          thumbnailUrl={thumbnailUrl}
          thumbnailNullText={thumbnailNullText}
          thumbnailChildren={thumbnailChildren}
          templateLinkProps={{
            linkTo,
            onClick,
            disabled: isDisabled,
          }}
        />
        {info && (
          <div className="listing-card-template-thumbnail-info">{info}</div>
        )}

        {note && (
          <div className="listing-card-template-thumbnail-note">{note}</div>
        )}
      </figure>
    </div>
  );
};

ListingCardTemplate.propTypes = propTypes;

export default ListingCardTemplate;
